import { useCallback, useEffect, useMemo, useRef, useState, type ComponentType } from 'react';
import { createPortal } from 'react-dom';
import { animate, motion } from 'motion/react';

export interface DialItem {
  id: string;
  label: string;
  icon: ComponentType<{ className?: string }>;
  run?: () => void;
  /** Items with children open a second ring: the tree. */
  children?: DialItem[];
}
export type OrbitItem = DialItem;

const R1 = { inner: 66, outer: 136 };
const R2 = { inner: 146, outer: 212 };
const TOP_SNAP = 0; // the selected item sits at twelve o'clock

function polar(r: number, deg: number) {
  const rad = ((deg - 90) * Math.PI) / 180;
  return { x: r * Math.cos(rad), y: r * Math.sin(rad) };
}
function arcPath(ri: number, ro: number, a0: number, a1: number) {
  const large = a1 - a0 > 180 ? 1 : 0;
  const p1 = polar(ro, a0);
  const p2 = polar(ro, a1);
  const p3 = polar(ri, a1);
  const p4 = polar(ri, a0);
  return `M${p1.x},${p1.y} A${ro},${ro} 0 ${large} 1 ${p2.x},${p2.y} L${p3.x},${p3.y} A${ri},${ri} 0 ${large} 0 ${p4.x},${p4.y} Z`;
}
/** Evenly spaced segments around `centre`: a few form an arc, many a full ring. */
function layout(n: number, centre = 0, maxSpan = 360) {
  const step = Math.min(72, maxSpan / Math.max(1, n));
  const span = step * n;
  const start = centre - span / 2;
  return Array.from({ length: n }, (_, i) => ({
    a0: start + i * step + 2,
    a1: start + (i + 1) * step - 2,
    mid: start + (i + 0.5) * step,
  }));
}
const norm360 = (a: number) => ((a % 360) + 360) % 360;
const signed180 = (a: number) => ((norm360(a) + 180) % 360) - 180;
function inArc(angle: number, a0: number, a1: number) {
  const a = norm360(angle);
  const s = norm360(a0);
  const e = s + (a1 - a0);
  return (a >= s && a <= e) || (a + 360 >= s && a + 360 <= e);
}

/**
 * The Dial: a ring of places and actions under your finger.
 * Tap an item, or turn the ring (drag around it) until what you want sits at the top and press
 * the centre. Items with children open a second ring. Opened by press-and-slide (marking mode)
 * the dial activates on release; opened by a tap or double-tap it stays until you choose.
 */
export function Dial({ origin, dragging, items, onClose }: { origin: { x: number; y: number }; dragging: boolean; items: DialItem[]; onClose: () => void }) {
  const [hot, setHot] = useState<{ ring: 1 | 2; index: number } | null>(null);
  const [openCat, setOpenCat] = useState<number | null>(null);
  const [tapMode, setTapMode] = useState(!dragging);
  const [rot, setRot] = useState(0);
  const rootRef = useRef<HTMLDivElement>(null);
  const moved = useRef(false);
  const turn = useRef<{
    id: number;
    a0: number;
    rot0: number;
    turned: boolean;
  } | null>(null);

  const size = (R2.outer + 70) * 2;
  const c = useMemo(() => {
    const pad = R2.outer + 24;
    return {
      x: Math.min(window.innerWidth - pad, Math.max(pad, origin.x)),
      y: Math.min(window.innerHeight - pad, Math.max(pad, origin.y)),
    };
  }, [origin]);

  const ring1 = useMemo(() => layout(items.length), [items.length]);
  const children = useMemo(() => (openCat !== null ? (items[openCat]?.children ?? []) : []), [openCat, items]);
  const ring2 = useMemo(() => layout(children.length, ring1[openCat ?? 0]?.mid ?? 0, 200), [children.length, openCat, ring1]);

  // The item nearest the top, after rotation, is the one the centre button opens.
  const list = openCat !== null ? children : items;
  const ring = openCat !== null ? ring2 : ring1;
  const selectedIndex = useMemo(() => {
    let best = -1;
    let bestD = 999;
    ring.forEach((s, i) => {
      const d = Math.abs(signed180(s.mid + rot - TOP_SNAP));
      if (d < bestD) {
        bestD = d;
        best = i;
      }
    });
    return best;
  }, [ring, rot]);
  const selected = list[selectedIndex];

  const activate = useCallback(
    (item: DialItem | undefined, index: number) => {
      if (!item) return;
      if (item.children?.length) {
        setOpenCat(index);
        setHot(null);
        return;
      }
      onClose();
      item.run?.();
    },
    [onClose],
  );

  const angleAt = useCallback((px: number, py: number) => norm360((Math.atan2(py - c.y, px - c.x) * 180) / Math.PI + 90), [c]);
  const hitTest = useCallback(
    (px: number, py: number) => {
      const dist = Math.hypot(px - c.x, py - c.y);
      const angle = angleAt(px, py) - rot;
      if (dist < R1.inner - 8) return null;
      if (openCat !== null && dist >= R2.inner - 6) {
        const i = ring2.findIndex((s) => inArc(angle, s.a0 - 2, s.a1 + 2));
        return i >= 0 ? { ring: 2 as const, index: i } : null;
      }
      const i = ring1.findIndex((s) => inArc(angle, s.a0 - 2, s.a1 + 2));
      return i >= 0 ? { ring: 1 as const, index: i } : null;
    },
    [c, angleAt, rot, ring1, ring2, openCat],
  );

  // Marking mode: follow the pointer that opened the dial, activate on release.
  useEffect(() => {
    if (tapMode) return;
    const onMove = (e: PointerEvent) => {
      if (Math.hypot(e.clientX - c.x, e.clientY - c.y) > 16) moved.current = true;
      const h = hitTest(e.clientX, e.clientY);
      setHot(h);
      if (h?.ring === 1 && items[h.index]?.children?.length) setOpenCat(h.index);
    };
    const onUp = (e: PointerEvent) => {
      const h = hitTest(e.clientX, e.clientY);
      if (!moved.current || !h) return setTapMode(true);
      if (h.ring === 2) activate(children[h.index], h.index);
      else if (!items[h.index]?.children?.length) activate(items[h.index], h.index);
      else setTapMode(true);
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    };
  }, [tapMode, c, hitTest, items, children, activate]);

  // Turning the ring: drag around the centre; release snaps the nearest item to the top.
  const snapTo = useCallback(
    (from: number) => {
      let best = from;
      let bestD = 999;
      for (const s of ring) {
        const d = signed180(TOP_SNAP - (s.mid + from));
        if (Math.abs(d) < bestD) {
          bestD = Math.abs(d);
          best = from + d;
        }
      }
      animate(from, best, {
        type: 'spring',
        stiffness: 420,
        damping: 32,
        onUpdate: (v) => setRot(v),
      });
    },
    [ring],
  );
  const onRingPointerDown = (e: React.PointerEvent) => {
    if (!tapMode) return;
    const dist = Math.hypot(e.clientX - c.x, e.clientY - c.y);
    if (dist < R1.inner - 8 || dist > R2.outer + 30) return;
    turn.current = {
      id: e.pointerId,
      a0: angleAt(e.clientX, e.clientY),
      rot0: rot,
      turned: false,
    };
  };
  const onRingPointerMove = (e: React.PointerEvent) => {
    const t = turn.current;
    if (!t || t.id !== e.pointerId) return;
    const d = signed180(angleAt(e.clientX, e.clientY) - t.a0);
    if (!t.turned && Math.abs(d) < 7) return;
    if (!t.turned) {
      t.turned = true;
      // Capture only once a turn has begun, so plain taps still reach the items.
      e.currentTarget.setPointerCapture(e.pointerId);
    }
    setRot(t.rot0 + d);
    setHot(null);
  };
  const onRingPointerUp = (e: React.PointerEvent) => {
    const t = turn.current;
    turn.current = null;
    if (!t || t.id !== e.pointerId) return;
    if (t.turned) {
      moved.current = true;
      snapTo(rot);
      setTimeout(() => (moved.current = false), 0);
    }
  };

  useEffect(() => {
    rootRef.current?.focus();
  }, []);

  const onKey = (e: React.KeyboardEvent) => {
    const cur = hot?.ring === (openCat !== null ? 2 : 1) ? hot.index : selectedIndex;
    if (e.key === 'Escape') {
      e.preventDefault();
      if (openCat !== null) setOpenCat(null);
      else onClose();
    } else if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
      e.preventDefault();
      const i = (cur + 1) % list.length;
      setHot({ ring: openCat !== null ? 2 : 1, index: i });
      snapTo(rot - (ring[i]!.mid - (ring[cur]?.mid ?? ring[i]!.mid)));
    } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
      e.preventDefault();
      const i = (cur - 1 + list.length) % list.length;
      setHot({ ring: openCat !== null ? 2 : 1, index: i });
      snapTo(rot - (ring[i]!.mid - (ring[cur]?.mid ?? ring[i]!.mid)));
    } else if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      activate(list[cur], cur);
    }
  };

  const hotItem = hot ? (hot.ring === 1 ? items[hot.index] : children[hot.index]) : null;
  const centreItem = hotItem ?? selected;
  const CentreIcon = centreItem?.icon;
  const isSel = (r: 1 | 2, i: number) => (openCat !== null ? r === 2 : r === 1) && i === selectedIndex && !hot;

  return createPortal(
    <div
      ref={rootRef}
      tabIndex={-1}
      role="dialog"
      aria-label="Dial: move to a place or action"
      onKeyDown={onKey}
      onPointerDown={(e) => {
        if (tapMode && e.target === e.currentTarget) onClose();
      }}
      className="nw-orbit-backdrop"
    >
      <motion.div
        className="nw-orbit"
        style={{
          left: c.x - size / 2,
          top: c.y - size / 2,
          width: size,
          height: size,
        }}
        initial={{ scale: 0.7, opacity: 0, rotate: -12 }}
        animate={{ scale: 1, opacity: 1, rotate: 0 }}
        transition={{ type: 'spring', stiffness: 380, damping: 28 }}
        onPointerDown={onRingPointerDown}
        onPointerMove={onRingPointerMove}
        onPointerUp={onRingPointerUp}
        onPointerCancel={onRingPointerUp}
      >
        <svg viewBox={`${-size / 2} ${-size / 2} ${size} ${size}`} width={size} height={size} aria-hidden>
          <g transform={`rotate(${rot})`}>
            {ring1.map((s, i) => (
              <path
                key={i}
                d={arcPath(R1.inner, R1.outer, s.a0, s.a1)}
                className={hot?.ring === 1 && hot.index === i ? 'nw-orbit-seg nw-orbit-seg-hot' : openCat === i ? 'nw-orbit-seg nw-orbit-seg-open' : isSel(1, i) ? 'nw-orbit-seg nw-orbit-seg-sel' : 'nw-orbit-seg'}
              />
            ))}
            {ring2.map((s, i) => (
              <path
                key={`r2-${i}`}
                d={arcPath(R2.inner, R2.outer, s.a0, s.a1)}
                className={hot?.ring === 2 && hot.index === i ? 'nw-orbit-seg nw-orbit-seg-hot' : isSel(2, i) ? 'nw-orbit-seg nw-orbit-seg-sel nw-orbit-seg-outer' : 'nw-orbit-seg nw-orbit-seg-outer'}
              />
            ))}
          </g>
          <circle r={R1.inner - 12} className="nw-orbit-dial" />
          <path d={`M0,${-(R1.inner - 4)} l-6,-9 h12 z`} className="nw-orbit-pointer" />
        </svg>
        <div role="menu" aria-label="Places and actions" className="absolute inset-0">
          {items.map((it, i) => {
            const p = polar((R1.inner + R1.outer) / 2, ring1[i]!.mid + rot);
            const Icon = it.icon;
            return (
              <button
                key={it.id}
                type="button"
                role="menuitem"
                aria-haspopup={it.children?.length ? 'menu' : undefined}
                onPointerEnter={() => tapMode && !turn.current && setHot({ ring: 1, index: i })}
                onClick={() => tapMode && !moved.current && activate(it, i)}
                className="nw-orbit-label"
                style={{ left: size / 2 + p.x, top: size / 2 + p.y }}
                data-hot={hot?.ring === 1 && hot.index === i ? 'true' : undefined}
              >
                <Icon className="size-[18px]" />
                <span>{it.label}</span>
              </button>
            );
          })}
          {children.map((it, i) => {
            const p = polar((R2.inner + R2.outer) / 2, ring2[i]!.mid + rot);
            const Icon = it.icon;
            return (
              <button
                key={it.id}
                type="button"
                role="menuitem"
                onPointerEnter={() => tapMode && !turn.current && setHot({ ring: 2, index: i })}
                onClick={() => !moved.current && activate(it, i)}
                className="nw-orbit-label"
                style={{ left: size / 2 + p.x, top: size / 2 + p.y }}
                data-hot={hot?.ring === 2 && hot.index === i ? 'true' : undefined}
              >
                <Icon className="size-[18px]" />
                <span>{it.label}</span>
              </button>
            );
          })}
          <button type="button" className="nw-orbit-centre" aria-live="polite" aria-label={centreItem ? `Open ${centreItem.label}` : 'Choose'} onClick={() => tapMode && activate(centreItem, hot ? hot.index : selectedIndex)}>
            {CentreIcon ? <CentreIcon className="size-5" /> : <span className="nw-orbit-live" />}
            <span className="max-w-[92px] truncate text-[11px] font-semibold">{centreItem?.label ?? (tapMode ? 'Turn or tap' : 'Slide')}</span>
          </button>
        </div>
      </motion.div>
    </div>,
    document.body,
  );
}

/** Long-press (touch or mouse) on an element itself, not its children, to open the dial there. */
export function useLongPress(onLongPress: (p: { x: number; y: number }) => void, ms = 450) {
  const timer = useRef<number | null>(null);
  const start = useRef<{ x: number; y: number } | null>(null);
  const clear = () => {
    if (timer.current) window.clearTimeout(timer.current);
    timer.current = null;
    start.current = null;
  };
  return {
    onPointerDown: (e: React.PointerEvent) => {
      if (e.target !== e.currentTarget || e.button > 0) return;
      start.current = { x: e.clientX, y: e.clientY };
      timer.current = window.setTimeout(() => {
        if (start.current) onLongPress(start.current);
        clear();
      }, ms);
    },
    onPointerMove: (e: React.PointerEvent) => {
      if (start.current && Math.hypot(e.clientX - start.current.x, e.clientY - start.current.y) > 10) clear();
    },
    onPointerUp: clear,
    onPointerCancel: clear,
    onContextMenu: (e: React.MouseEvent) => {
      if (e.target === e.currentTarget) {
        e.preventDefault();
        onLongPress({ x: e.clientX, y: e.clientY });
      }
    },
  };
}

/** Double-tap (or double-click) on an element itself, not its children. */
export function useDoubleTap(onDoubleTap: (p: { x: number; y: number }) => void) {
  const last = useRef<{ t: number; x: number; y: number } | null>(null);
  return {
    onPointerUp: (e: React.PointerEvent) => {
      if (e.target !== e.currentTarget || e.button > 0) return;
      const now = performance.now();
      const prev = last.current;
      last.current = { t: now, x: e.clientX, y: e.clientY };
      if (prev && now - prev.t < 320 && Math.hypot(e.clientX - prev.x, e.clientY - prev.y) < 30) {
        last.current = null;
        onDoubleTap({ x: e.clientX, y: e.clientY });
      }
    },
  };
}
